"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import BeneficiaryModal from "./BeneficiaryModal";
import { getCountries, type Country } from "@/lib/supabase-queries";

const ArrowLeftRightIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
    />
  </svg>
);

const UserPlusIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </svg>
);

export const ActionButtonsPanel: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);

  // Load countries when component mounts
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoading(true);
        const countriesData = await getCountries();
        setCountries(countriesData);
      } catch (error) {
        console.error("Error loading countries:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCountries();
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveBeneficiary = () => {
    // Close modal and refresh data if needed
    setIsModalOpen(false);
    // You could add a callback here to refresh the beneficiaries list
  };

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-8 max-w-[538px]">
      {/* Primary Action - Individual Transfer */}
      <Link href="/transfer" className="flex-1">
        <button className="h-[76px] bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm p-4 transition-colors w-[261px]">
          <div className="flex items-center gap-4 w-full">
            <div className="w-11 h-11 bg-black rounded-full flex items-center justify-center flex-shrink-0">
              <ArrowLeftRightIcon />
            </div>
            <div className="text-left flex-1">
              <div className="font-lato text-base font-normal">
                Individual Transfer
              </div>
              <div className="font-lato text-sm font-light">to any country</div>
            </div>
          </div>
        </button>
      </Link>

      {/* Secondary Action - Add Recipients */}
      <button
        onClick={handleOpenModal}
        disabled={loading}
        className="flex-1 w-full h-[76px] bg-[#DBE5F7] border border-gray-200 hover:bg-gray-100 text-gray-900 rounded-lg shadow-2xs p-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-4 w-full">
          <div className="w-11 h-11 bg-black rounded-full flex items-center justify-center text-white flex-shrink-0">
            <UserPlusIcon />
          </div>
          <div className="text-left flex-1">
            <div className="font-lato text-base font-normal">
              Add Recipients
            </div>
            <div className="font-lato text-sm font-light">new ones</div>
          </div>
        </div>
      </button>

      {/* Beneficiary Modal */}
      {isModalOpen && (
        <BeneficiaryModal
          beneficiary={null}
          isEditing={false}
          countries={countries}
          onClose={handleCloseModal}
          onSave={handleSaveBeneficiary}
        />
      )}
    </div>
  );
};

export default ActionButtonsPanel;
